import { type KeyboardEvent, useState } from 'react';
import { t } from '@lingui/macro';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Image } from '@rover/kibble/core';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';

import { GALLERY_IMAGE_SIZE } from './constants';

type ImageWithShimmerProps = {
  src: string;
  caption?: string;
  width?: number;
  height?: number;
  onClick?: () => void;
  cached?: boolean;
  setCached?: (cached: boolean) => void;
};
const ImageWithShimmer = ({
  src,
  caption,
  width = parseInt(GALLERY_IMAGE_SIZE, 10),
  height = parseInt(GALLERY_IMAGE_SIZE, 10),
  onClick,
  cached,
  setCached,
}: ImageWithShimmerProps): JSX.Element => {
  const [loaded, setLoaded] = useState(cached ?? false);
  const clickable = !!onClick;

  return (
    <Box
      position="relative"
      width={width}
      height={height}
      top="0"
      left="0"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Box
        data-testid="open-light-box"
        onClick={onClick}
        role={clickable ? 'button' : undefined}
        webProps={{
          tabIndex: clickable ? 0 : undefined,
          onKeyDown: clickable
            ? (e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === KEYS.ENTER || e.key === KEYS.SPACE) {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined,
        }}
        sx={{ cursor: clickable ? 'pointer' : 'default' }}
      >
        <Image
          src={src}
          description={caption || t`image`}
          resizeMethod="contain"
          width={width}
          height={height}
          borderRadius="primary"
          onLoad={() => {
            setLoaded(true);
            if (setCached) setCached(true);
          }}
        />
      </Box>

      {!loaded && (
        <Box
          width={width}
          height={height}
          position="absolute"
          top="0"
          left="0"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingShimmer
            width={width}
            height={height}
            borderRadius="primary"
            m="0x"
            data-testid="loading-shimmer"
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageWithShimmer;
